// Dialog for editing a category node (name, description, ownership role).

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { ownershipRolesApi } from '@/lib/api/ownershipRoles'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'

interface EditCategoryDialogProps {
  open: boolean
  currentName: string
  currentDescription: string
  currentOwnerRoleId: string
  onConfirm: (name: string, description: string, ownerRoleId: string) => void
  onCancel: () => void
  isPending?: boolean
}

export function EditCategoryDialog({
  open,
  currentName,
  currentDescription,
  currentOwnerRoleId,
  onConfirm,
  onCancel,
  isPending,
}: EditCategoryDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const [name, setName] = useState(currentName)
  const [description, setDescription] = useState(currentDescription)
  const [ownerRoleId, setOwnerRoleId] = useState(currentOwnerRoleId)

  // Sync local state when the dialog opens with new values
  useEffect(() => {
    if (open) {
      setName(currentName)
      setDescription(currentDescription)
      setOwnerRoleId(currentOwnerRoleId)
    }
  }, [open, currentName, currentDescription, currentOwnerRoleId])

  const rolesQuery = useQuery({
    queryKey: ['tenant', slug, 'ownership-roles'],
    queryFn: () => ownershipRolesApi(api).list(),
    enabled: open,
  })

  const roles = rolesQuery.data ?? []

  const hasChanges =
    name.trim() !== currentName ||
    description.trim() !== currentDescription ||
    ownerRoleId !== currentOwnerRoleId
  const canSubmit = name.trim() && ownerRoleId && hasChanges && !isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (canSubmit) {
      onConfirm(name.trim(), description.trim(), ownerRoleId)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
          <DialogDescription>Changes are saved immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (optional)</Label>
              <Input
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-owner-role">Owner Role</Label>
              {rolesQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading roles...
                </div>
              ) : (
                <select
                  id="edit-owner-role"
                  value={ownerRoleId}
                  onChange={(e) => setOwnerRoleId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select an ownership role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                      {role.owner_display_name ? ` (${role.owner_display_name})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
